import { useEffect, useRef, useState } from "preact/hooks";
import { loadGoogleMaps } from "./mapsLoader";
import { footprintPixels } from "./footprintScale";
import type { OverlayDimensions, Placement } from "./types";
import { GOOGLE_MAPS_MAP_ID } from "../env";

interface Props {
  /** address to geocode and center on (ignored when `initial` is provided) */
  address: string;
  /** real overlay size in meters — drives the to-scale rectangle (SKU-agnostic) */
  overlay: OverlayDimensions;
  /** stored placement to re-render exactly (the fidelity proof); geocode is skipped */
  initial?: Placement | null;
  /** fires whenever the placement changes (drag / zoom / pan / rotate) */
  onChange?: (p: Placement) => void;
}

const DEFAULT_ZOOM = 20;

/** AdvancedMarkerElement.position is a LatLng (methods) after a drag, or a literal
 *  (number props) when set programmatically. Normalize to plain numbers. */
function toLatLng(
  pos: google.maps.marker.AdvancedMarkerElement["position"],
): { lat: number; lng: number } {
  const p = pos as google.maps.LatLng | google.maps.LatLngLiteral | null;
  if (!p) return { lat: 0, lng: 0 };
  const lat = typeof (p as google.maps.LatLng).lat === "function"
    ? (p as google.maps.LatLng).lat()
    : (p as google.maps.LatLngLiteral).lat;
  const lng = typeof (p as google.maps.LatLng).lng === "function"
    ? (p as google.maps.LatLng).lng()
    : (p as google.maps.LatLngLiteral).lng;
  return { lat, lng };
}

export function AerialPlacement({ address, overlay, initial, onChange }: Props) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const overlayElRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<number>(initial?.item.headingDeg ?? 0);

  const [heading, setHeading] = useState<number>(initial?.item.headingDeg ?? 0);
  const [error, setError] = useState<string | null>(null);

  // Screen-anchored markers don't scale with zoom — resize the overlay on demand.
  const rescaleOverlay = () => {
    const map = mapRef.current;
    const el = overlayElRef.current;
    const marker = markerRef.current;
    if (!map || !el || !marker) return;
    const zoom = map.getZoom() ?? DEFAULT_ZOOM;
    const pos = toLatLng(marker.position);
    el.style.width = `${footprintPixels(overlay.widthMeters, pos.lat, zoom)}px`;
    el.style.height = `${footprintPixels(overlay.depthMeters, pos.lat, zoom)}px`;
  };

  const publish = () => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const c = map.getCenter();
    const pos = toLatLng(marker.position);
    onChange?.({
      view: {
        center: { lat: c?.lat() ?? 0, lng: c?.lng() ?? 0 },
        zoom: map.getZoom() ?? DEFAULT_ZOOM,
        mapTypeId: "hybrid",
      },
      item: { lat: pos.lat, lng: pos.lng, headingDeg: headingRef.current },
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const maps = await loadGoogleMaps();
        if (cancelled || !mapDivRef.current) return;

        // Maps JS with loading=async exposes classes via importLibrary, not synchronously.
        const { Map } = (await maps.importLibrary("maps")) as google.maps.MapsLibrary;
        const { AdvancedMarkerElement } = (await maps.importLibrary("marker")) as google.maps.MarkerLibrary;
        const { Geocoder } = (await maps.importLibrary("geocoding")) as google.maps.GeocodingLibrary;
        if (cancelled || !mapDivRef.current) return;

        // Stored view wins (re-render fidelity); otherwise geocode the address.
        let center: google.maps.LatLngLiteral;
        let zoom = DEFAULT_ZOOM;
        if (initial) {
          center = initial.view.center;
          zoom = initial.view.zoom;
        } else {
          const { results } = await new Geocoder().geocode({ address });
          const loc = results[0]?.geometry.location;
          if (!loc) throw new Error("address did not geocode");
          center = { lat: loc.lat(), lng: loc.lng() };
        }

        const map = new Map(mapDivRef.current, {
          center,
          zoom,
          mapTypeId: "hybrid",
          tilt: 0,
          mapId: GOOGLE_MAPS_MAP_ID, // AdvancedMarkerElement requires a Map ID
        });
        mapRef.current = map;

        const el = document.createElement("div");
        el.className = "aerial-overlay";
        el.style.transform = `translate(0, 50%) rotate(${headingRef.current}deg)`;
        overlayElRef.current = el;

        const start = initial?.item ?? { lat: center.lat, lng: center.lng };
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: start.lat, lng: start.lng },
          content: el,
          gmpDraggable: true,
        });
        markerRef.current = marker;

        rescaleOverlay();
        publish();

        map.addListener("zoom_changed", () => { rescaleOverlay(); publish(); });
        map.addListener("center_changed", publish);
        marker.addListener("dragend", () => { rescaleOverlay(); publish(); });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotation slider → overlay transform + republish, via the ref (no stale closure).
  useEffect(() => {
    headingRef.current = heading;
    if (overlayElRef.current) overlayElRef.current.style.transform = `translate(0, 50%) rotate(${heading}deg)`;
    publish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heading]);

  if (error) {
    return (
      <div class="aerial-placement-error">
        <p><strong>Map unavailable:</strong> {error}</p>
        <p class="muted">Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>web/.env.local</code> and reload.</p>
      </div>
    );
  }

  return (
    <div class="aerial-placement">
      <div class="aerial-map" ref={mapDivRef} />
      <label class="aerial-rotate">
        Rotation: {heading}°
        <input
          type="range" min={0} max={359} value={heading}
          onInput={(e) => setHeading(Number((e.currentTarget as HTMLInputElement).value))}
        />
      </label>
    </div>
  );
}
